import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleLogin } from '@react-oauth/google';
import { Box, Button, Select, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import { connectSocket } from '../../../../store/socketConfigSlice';
import GoogleDriveIcon from '../../../../assets/uploadFile-assets/google-drive.svg';
import OneDriveIcon from '../../../../assets/uploadFile-assets/one-drive.svg';
import SharePointIcon from '../../../../assets/uploadFile-assets/sharepoint.svg';
import GmailIcon from '../../../../assets/uploadFile-assets/gmail.svg';
import OutlookIcon from '../../../../assets/uploadFile-assets/outlook.svg';
import Toast from '../../utils/Toast';
import { DOCUMENT_TOAST } from '../../../../constants/toastConstant';
import { UPLOAD } from '../../../../constants/staticContent';
import {
  Root,
  MenuItemUpload,
  IconBox,
  ListItemTypo,
  StyledMenuPropsForChooseButton,
  StyledMenuPropsForUploadButton,
  classes,
} from './utils';
import googleDriveUpload from './googleDriveUpload';
import oneDrivePicker from './oneDrivePicker';
import {
  uploadDocument,
  manualUploadDocumentFromMicrosoft,
  getUserEmailFromAccessToken,
} from '../../../../services/uploadApi';
import MailListPopup from './MailListPopup';
import FilterPopUp from './FilterPopUp';
import { setIsUploaded } from '../../../../store/reloadOnUploadSlice';
import { globalClasses } from '../../utils/globalUtils';
import {
  connectDocumentSocket,
  disconnectDocumentSocket,
} from '../../../../store/documentUploadSlice';
import { setDocumentData } from '../../../../store/newDocumentSlice';
import {
  microsoftAuthentication,
  tokenExchangeForMicrosoft,
} from '../../../../services/flowConfiguration';
import MODELS from '../../../../constants/models';
import CustomTooltip from '../../utils/CustomTooltip';

const UploadDocument = ({
  uploadButtonTypo,
  setUploadPending,
  documentType,
}) => {
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isConnected = useSelector((state) => state.socketConfig.isConnected);
  const userDetail = useSelector((state) => state.user.user);
  const userId = userDetail?.id;
  const [pickerApiLoaded, setPickerApiLoaded] = useState(false);
  const [showQueryDialog, setShowQueryDialog] = useState(false);
  const [isOutlookSelectedForUpload, setIsOutlookSelectedForUpload] =
    useState(false);
  const [isGmailSelectedForUpload, setIsGmailSelectedForUpload] =
    useState(false);
  const [accessTokenForOutlook, setAccessTokenForOutlook] = useState('');
  const [accessTokenForGmail, setAccessTokenForGmail] = useState('');
  const [openMailDialog, setOpenMailDialog] = useState(false);
  const [uploadOption, setUploadOption] = useState('');
  const isUploadDocumentConnected = useSelector(
    (state) => state.documentUploadConfig.isUploadDocumentConnected,
  );

  const uploadDocumentSocket = useSelector(
    (state) => state.latedUploadedDocument.uploadDocumentSocket,
  );
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const importOptionFromSessionStorage =
    sessionStorage.getItem('importManualOption');
  const getFileFromOneDrive = sessionStorage.getItem('oneDriveSelectedFile');
  const outlookUploadAccessToken = sessionStorage.getItem(
    'outlookUploadAccessToken',
  );

  useEffect(() => {
    const loadPickerApi = () => {
      window.gapi.load('picker', () => {
        setPickerApiLoaded(true);
      });
    };

    if (!window.gapi) {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('auth', loadPickerApi);
      };
      document.body.appendChild(script);
    } else {
      loadPickerApi();
    }
  }, []);

  const handleButtonClick = () => {
    setUploadOption('Local Device');
    fileInputRef.current.value = null;
    fileInputRef.current.click();
  };

  const config = {
    headers: {
      'content-type': 'multipart/form-data',
    },
  };

  const handleOneDrivePicker = async (accessTokens) => {
    const oneDriveFuncData = await oneDrivePicker(accessTokens);
    if (oneDriveFuncData && oneDriveFuncData?.files?.length > 0) {
      sessionStorage.setItem(
        'oneDriveSelectedFile',
        JSON.stringify(oneDriveFuncData),
      );
      if (documentType === MODELS.INVOICE) {
        navigate('/invoices');
      } else if (documentType === MODELS.RECEIPT) {
        navigate('/receipts');
      } else if (documentType === MODELS.SALES) {
        navigate('/sales');
      } else if (documentType === MODELS.CREDIT_CARD_STATEMENT) {
        navigate('/creditcardstatements');
      } else if (documentType === MODELS.BANK_STATEMENT) {
        navigate('/bank-statements');
      }
    }
  };
  const handleOutlook = (accessToken) => {
    sessionStorage.setItem('outlookUploadAccessToken', accessToken);
    if (documentType === MODELS.INVOICE) {
      navigate('/invoices');
    } else if (documentType === MODELS.RECEIPT) {
      navigate('/receipts');
    } else if (documentType === MODELS.SALES) {
      navigate('/sales');
    } else if (documentType === MODELS.CREDIT_CARD_STATEMENT) {
      navigate('/creditcardstatements');
    } else if (documentType === MODELS.BANK_STATEMENT) {
      navigate('/bank-statements');
    }
  };
  useEffect(() => {
    if (outlookUploadAccessToken?.length) {
      setUploadOption(importOptionFromSessionStorage);
      setIsOutlookSelectedForUpload(true);
      setAccessTokenForOutlook(outlookUploadAccessToken);
      setShowQueryDialog(true);
    }
  }, [outlookUploadAccessToken]);
  const handleDocumentUpload = async () => {
    const parsedData = JSON.parse(getFileFromOneDrive);
    if (parsedData && parsedData?.files?.length) {
      if (isConnected === false) {
        dispatch(connectSocket(userId));
      }
      if (isUploadDocumentConnected === false) {
        dispatch(connectDocumentSocket({ userId, dispatch }));
      }

      const { accessToken, files, folder, email } = parsedData;
      try {
        const response = await manualUploadDocumentFromMicrosoft({
          accessToken,
          documentType,
          documentImportOption: importOptionFromSessionStorage,
          files,
          folder,
          documentImportCredential: email,
        });
        if (response.status === 500) {
          dispatch(setIsUploaded((prev) => !prev));
          dispatch(disconnectDocumentSocket());
          dispatch(setDocumentData(null));
        }
        if (response) {
          setTimeout(() => {
            dispatch(disconnectDocumentSocket());
            dispatch(setDocumentData(null));
          }, 500);
        }
      } catch (error) {
        console.error('Error uploading document:', error);
      }
    }
  };
  useEffect(() => {
    if (getFileFromOneDrive?.length) {
      handleDocumentUpload();
    }
  }, [getFileFromOneDrive]);
  const handleAccessTokenCallBack = async () => {
    if (
      importOptionFromSessionStorage === 'OneDrive' ||
      importOptionFromSessionStorage === 'SharePoint' ||
      importOptionFromSessionStorage === 'Outlook'
    ) {
      const tokenData = await tokenExchangeForMicrosoft(code, documentType);
      if (tokenData) {
        if (
          importOptionFromSessionStorage === 'OneDrive' ||
          importOptionFromSessionStorage === 'SharePoint'
        ) {
          handleOneDrivePicker(tokenData?.accessToken);
        } else {
          handleOutlook(tokenData?.accessToken);
        }
      }
    }
  };
  useEffect(() => {
    if (code) {
      handleAccessTokenCallBack();
    }
  }, [code]);

  const handleFileUpload = (event) => {
    const uploadEventFiles = event.target.files;
    const allowedFileTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ];
    const maxSizeInBytes = 10 * 1024 * 1024; // max size allowed 10mb

    const multipleFilesArray = Array.from(uploadEventFiles);
    const filesFormatNotValid = multipleFilesArray.filter(
      (file) => !allowedFileTypes.includes(file.type),
    );
    filesFormatNotValid?.forEach((file) => {
      Toast({
        message: `"${file.name}" ${DOCUMENT_TOAST.FORMAT_INVALID}`,
        variant: 'error',
        position: { vertical: 'top', horizontal: 'center' },
        autoHideDuration: 4000,
      });
    });
    const filesFormatValid = multipleFilesArray.filter((file) =>
      allowedFileTypes.includes(file.type),
    );

    const filesSizeTooLarge = filesFormatValid?.filter(
      (file) => file.size > maxSizeInBytes,
    );
    filesSizeTooLarge?.forEach((file) => {
      Toast({
        message: `"${file.name}" ${DOCUMENT_TOAST.FILE_SIZE_TOO_LARGE}`,
        variant: 'error',
        position: { vertical: 'top', horizontal: 'center' },
        autoHideDuration: 4000,
      });
    });

    const sizeLimitFilesValid = filesFormatValid?.filter(
      (file) => file.size <= maxSizeInBytes,
    );

    const fileNamesWithSpacesOrSpecialChars = sizeLimitFilesValid?.filter(
      (file) => /[ !@#$%^&*]/.test(file.name),
    );

    fileNamesWithSpacesOrSpecialChars?.forEach((file) => {
      Toast({
        message: `"${file.name}" ${DOCUMENT_TOAST.SPACES_OR_SPECIAL_CHARS_NOT_ALLOWED}`,
        variant: 'error',
        position: { vertical: 'top', horizontal: 'center' },
        autoHideDuration: 4000,
      });
    });

    const fileNamesWithoutSpacesOrSpecialChars = sizeLimitFilesValid?.filter(
      (file) => /^[^\s!@#$%^&*]+$/.test(file.name),
    );

    const formData = new FormData();
    fileNamesWithoutSpacesOrSpecialChars?.forEach((file) => {
      formData.append('upload', file);
    });
    // Add DocumentType to formData
    formData.append('documentType', documentType);
    formData.append('documentImportOption', uploadOption);
    if (
      fileNamesWithoutSpacesOrSpecialChars &&
      fileNamesWithoutSpacesOrSpecialChars.length > 0
    ) {
      setUploadPending(true);
      if (isConnected === false) {
        dispatch(connectSocket(userId));
      }
      if (isUploadDocumentConnected === false) {
        dispatch(connectDocumentSocket({ userId, dispatch }));
      }
      uploadDocument({ config, formData }).then(() => {
        setTimeout(() => {
          dispatch(setIsUploaded((prev) => !prev));
          dispatch(disconnectDocumentSocket());
          dispatch(setDocumentData(null));
        }, 500);
      });
    }
  };
  useEffect(() => {
    if (uploadDocumentSocket) {
      dispatch(setIsUploaded((prev) => !prev));
      setUploadPending(false);
      if (getFileFromOneDrive?.length) {
        sessionStorage.removeItem('importManualOption');
        sessionStorage.removeItem('oneDriveSelectedFile');
      }
      if (outlookUploadAccessToken?.length) {
        sessionStorage.removeItem('importManualOption');
        sessionStorage.removeItem('outlookUploadAccessToken');
      }
    }
  }, [uploadDocumentSocket]);

  const handleGoogleDriveSelection = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      (async () => {
        try {
          const accessTokenForGoogleDrive = tokenResponse?.access_token;
          if (!accessTokenForGoogleDrive) {
            throw new Error('Access token is missing');
          }
          const email = await getUserEmailFromAccessToken(
            accessTokenForGoogleDrive,
          );
          const selectedFiles = await googleDriveUpload(
            pickerApiLoaded,
            accessTokenForGoogleDrive,
          );
          if (!selectedFiles || selectedFiles.length === 0) {
            throw new Error('No files selected');
          }

          const formData = new FormData();
          selectedFiles.forEach((file) => {
            formData.append('upload', file);
          });
          formData.append('documentType', documentType);
          formData.append('documentImportOption', uploadOption);
          formData.append('documentImportCredential', email);
          if (isConnected === false) {
            dispatch(connectSocket(userId));
          }
          if (isUploadDocumentConnected === false) {
            dispatch(connectDocumentSocket({ userId, dispatch }));
          }
          setUploadPending(true);
          const uploadRes = await uploadDocument({ formData, config });
          if (uploadRes.status === 500) {
            dispatch(setIsUploaded((prev) => !prev));
            dispatch(disconnectDocumentSocket());
            dispatch(setDocumentData(null));
          }
          if (uploadRes) {
            setTimeout(() => {
              dispatch(disconnectDocumentSocket());
              dispatch(setDocumentData(null));
            }, 500);
          }
        } catch (error) {
          console.error('Error during Google Drive selection:', error);
        }
      })();
    },
    onFailure: (error) => {
      console.error('Google login error:', error);
    },
    access_type: 'offline',
    scope: ['https://www.googleapis.com/auth/drive.readonly'],
  });

  const handleOneDriveSelection = async (uploadType) => {
    const responseUrl = await microsoftAuthentication(documentType);
    if (responseUrl) {
      sessionStorage.setItem('importManualOption', uploadType);
      window.location.href = `${responseUrl.url}`;
    }
  };
  const handleGmailLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      (async () => {
        const accessToken = tokenResponse?.access_token;
        setIsGmailSelectedForUpload(true);
        setAccessTokenForGmail(accessToken);
        setShowQueryDialog(true);
      })();
    },
    onFailure: (error) => {
      console.error('error', error);
      Toast({
        message: 'Failed to authenticate with Gmail.',
        variant: 'error',
        position: { vertical: 'top', horizontal: 'center' },
        autoHideDuration: 4000,
      });
    },
    scope: 'https://www.googleapis.com/auth/gmail.readonly',
  });

  const handleClose = async () => {
    setShowQueryDialog(false);
  };
  const handlePopupClose = () => {
    setOpenMailDialog(false);
  };

  const uploadOptions = [
    {
      id: 1,
      optionName: 'GoogleDrive',
      icon: GoogleDriveIcon,
      function: handleGoogleDriveSelection,
    },
    {
      id: 2,
      optionName: 'OneDrive',
      icon: OneDriveIcon,
      function: () => handleOneDriveSelection('OneDrive'),
    },
    {
      id: 3,
      optionName: 'SharePoint',
      icon: SharePointIcon,
      function: () => handleOneDriveSelection('SharePoint'),
    },
    {
      id: 4,
      optionName: 'Gmail',
      icon: GmailIcon,
      function: handleGmailLogin,
    },
    {
      id: 5,
      optionName: 'Outlook',
      icon: OutlookIcon,
      function: () => handleOneDriveSelection('Outlook'),
    },
  ];

  const handleUploadChange = (value) => {
    setUploadOption(value);
  };

  return (
    <Root>
      <Box className={globalClasses.displayFlexAlignJustifyContent}>
        <input
          type="file"
          ref={fileInputRef}
          className="upload-input"
          onChange={handleFileUpload}
          multiple
          accept=".jpg, .jpeg, .png, .pdf"
        />
        <CustomTooltip title={uploadButtonTypo || ''}>
          <Button
            variant="contained"
            className={classes.uploadButton}
            onClick={handleButtonClick}
          >
            <FileUploadOutlinedIcon className={classes.uploadIcon} />
            <Typography variant="body1" className={classes.buttonsTypo}>
              {uploadButtonTypo || ''}
            </Typography>
          </Button>
        </CustomTooltip>
        <Select
          className={classes.uploadButtonSelection}
          value=""
          IconComponent={KeyboardArrowDownIcon}
          onChange={(event) => handleUploadChange(event.target.value)}
          MenuProps={
            uploadButtonTypo === UPLOAD.UPLOAD
              ? StyledMenuPropsForUploadButton
              : StyledMenuPropsForChooseButton
          }
        >
          {uploadOptions?.map((option) => (
            <MenuItemUpload
              key={option.id}
              value={option.optionName}
              onClick={option.function}
            >
              <IconBox component="img" src={option.icon} alt="icon" />
              <ListItemTypo variant="body1">{option.optionName}</ListItemTypo>
            </MenuItemUpload>
          ))}
        </Select>
      </Box>
      {openMailDialog && (
        <MailListPopup
          open={openMailDialog}
          handlePopupClose={handlePopupClose}
          documentType={documentType}
          uploadOption={uploadOption}
        />
      )}
      {showQueryDialog && (
        <FilterPopUp
          open={showQueryDialog}
          handleClose={handleClose}
          isOutlookSelectedForUpload={isOutlookSelectedForUpload}
          isGmailSelectedForUpload={isGmailSelectedForUpload}
          accessTokenForOutlook={accessTokenForOutlook}
          accessTokenForGmail={accessTokenForGmail}
          documentType={documentType}
          uploadOption={uploadOption}
        />
      )}
    </Root>
  );
};

UploadDocument.propTypes = {
  uploadButtonTypo: PropTypes.string.isRequired,
  setUploadPending: PropTypes.func.isRequired,
  documentType: PropTypes.string.isRequired,
};

export default UploadDocument;
